import React, { useEffect, useRef, useState } from 'react';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Mask from '../../../helper/masksPure';
import { messageErrorDefault } from '../../../helper/constants';
import Validate from '../../../helper/validateUtils';
import { insertServiceProvider, getAddressByCep, listServiceTypes } from '../../../service/serviceProviderService';
import shieldIcon from '../../../assets/images/new_imobiliaria/shield.svg';
import TermsPage from '../terms/termsPage';
import { termsPages } from '../terms/termsDocuments';
import useCompany from '../useCompany';
import './serviceProvider.scss';

const steps = [
  'Acesso',
  'Dados Cadastrais',
  'Endereço, Contato e Pagamento',
  'Revisões e envio',
];

const fieldPlaceholders = {
  'E-mail': 'Digite seu e-mail',
  'Senha': 'Digite sua senha',
  'Confirmar senha': 'Digite sua senha novamente',
  'Nome Completo': 'Digite seu nome completo',
  'CPF': 'Digite seu CPF',
  'RG': 'Digite seu RG',
  'Razão Social / Nome Fantasia': 'Digite a razão social ou nome fantasia',
  'CNPJ': 'Digite o CNPJ',
  'Nome do Responsável': 'Digite o nome do responsável',
  'CPF do Responsável': 'Digite o CPF do responsável',
  'RG do Responsável': 'Digite o RG do responsável',
  'Tempo de atuação na área (em anos)': 'Informe o tempo de atuação',
  'Descrição dos serviços': 'Descreva os serviços prestados',
  'Descrição da empresa / serviços': 'Descreva a empresa e os serviços prestados',
  'CEP': 'Digite o CEP',
  'Logradouro': 'Digite o logradouro',
  'Número': 'Digite o número',
  'Complemento (Opcional)': 'Digite o complemento',
  'Bairro': 'Digite o bairro',
  'Cidade': 'Digite a cidade',
  'Estado': 'Selecione o estado',
  'Telefone': 'Digite o telefone',
  'Informe sua chave pix': 'Digite sua chave Pix',
};

const statesByUf = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia',
  CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
  MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
  PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
  RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
  RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo',
  SE: 'Sergipe', TO: 'Tocantins',
};

const brazilianStates = Object.values(statesByUf).sort((a, b) => a.localeCompare(b, 'pt-BR'));

const ufByStateName = Object.keys(statesByUf).reduce((map, uf) => ({ ...map, [statesByUf[uf]]: uf }), {});

const onlyDigits = value => value.replace(/\D/g, '');

const formatCpf = value => {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatCnpj = value => {
  const digits = onlyDigits(value).slice(0, 14);

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatCep = value => {
  const digits = onlyDigits(value).slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const formatPhone = value => {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 2) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

function ServiceProvider({ location, match }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [providerType, setProviderType] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [accessForm, setAccessForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [registrationForm, setRegistrationForm] = useState({
    fullName: '',
    cpf: '',
    rg: '',
    companyName: '',
    cnpj: '',
    responsibleName: '',
    responsibleCpf: '',
    responsibleRg: '',
    services: [],
    experience: '',
    description: '',
  });
  const [addressForm, setAddressForm] = useState({
    cep: '',
    street: '',
    number: '',
    noNumber: false,
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    phone: '',
    pixKey: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [serviceOptionsError, setServiceOptionsError] = useState('');

  const { companyId, theme } = useCompany();

  // Os tipos de serviço vêm do mesmo cadastro que o app consome, então a lista
  // acompanha o que a imobiliária mantém na API.
  useEffect(() => {
    let active = true;

    listServiceTypes(companyId)
      .then(serviceTypes => {
        if (!active) return;
        setServiceOptions(serviceTypes);
      })
      .catch(() => {
        if (!active) return;
        setServiceOptionsError('Não foi possível carregar os tipos de serviço. Tente novamente mais tarde.');
      });

    return () => {
      active = false;
    };
  }, [companyId]);

  const nextStep = () => setCurrentStep(step => Math.min(step + 1, steps.length));
  const previousStep = () => setCurrentStep(step => Math.max(step - 1, 1));

  // Aqui os termos continuam em rotas filhas do cadastro, e nao em /realEstate/termos:
  // como a rota do cadastro nao e exact, este componente segue montado e o que ja
  // foi preenchido nao se perde. A tela e o texto sao os mesmos das duas rotas.
  // match.url (e nao match.path) para levar o companyId real; sem a barra final,
  // que vem quando o endereco termina em "/".
  const basePath = ((match && match.url) || '/realEstate/serviceProvider').replace(/\/$/, '');
  const currentPath = (location && location.pathname) || basePath;
  const openTermsPage = termsPages.find(page => currentPath === `${basePath}/${page.slug}`);
  const termsPaths = termsPages.reduce(
    (paths, page) => ({ ...paths, [page.title]: `${basePath}/${page.slug}` }),
    {},
  );

  const buildPayload = () => {
    const isLegal = providerType === 'legal';

    return {
      companyId,
      user: {
        name: isLegal ? registrationForm.companyName : registrationForm.fullName,
        email: accessForm.email,
        password: accessForm.password,
        cnpjCpf: Mask.removeMask(isLegal ? registrationForm.cnpj : registrationForm.cpf),
        rg: onlyDigits(isLegal ? registrationForm.responsibleRg : registrationForm.rg),
      },
      phone: Mask.removeMask(addressForm.phone),
      pixKey: addressForm.pixKey,
      description: registrationForm.description,
      serviceTime: parseInt(registrationForm.experience, 10) || 0,
      serviceTypes: registrationForm.services.map(service => service.id),
      isLegalPerson: isLegal,
      responsible: isLegal
        ? {
          name: registrationForm.responsibleName,
          cnpjCpf: Mask.removeMask(registrationForm.responsibleCpf),
          rg: onlyDigits(registrationForm.responsibleRg),
        }
        : null,
      address: {
        cep: Mask.removeMask(addressForm.cep),
        address: addressForm.street,
        number: addressForm.noNumber ? 'S/N' : addressForm.number,
        complement: addressForm.complement,
        neighborhood: addressForm.neighborhood,
        cityName: addressForm.city,
        uf: ufByStateName[addressForm.state] || addressForm.state,
      },
    };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await insertServiceProvider(buildPayload());

      setSubmitted(true);

      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'serviceProvider:registered',
          id: response && response.id,
        }));
      }
    } catch (error) {
      const message = error.message || messageErrorDefault;

      setSubmitError(message);
      toast.warn(message, {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: 'ui-alert-toast',
        bodyClassName: 'ui-alert-toast-content',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (openTermsPage) {
    return (
      <div className="service-provider">
        <Hero theme={theme} />
        <TermsPage title={openTermsPage.title} audience="prestador" backTo={basePath} />
      </div>
    );
  }

  return (
    <div className="service-provider">
      <Hero theme={theme} />
      <main className="service-provider__content">
        <Stepper currentStep={currentStep} />
        {currentStep === 1 && (
          <AccessStep
            companyName={theme.shortName}
            form={accessForm}
            setForm={setAccessForm}
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
            termsPaths={termsPaths}
            onNext={nextStep}
          />
        )}
        {currentStep === 2 && (
          <RegistrationStep
            providerType={providerType}
            onProviderTypeChange={setProviderType}
            form={registrationForm}
            setForm={setRegistrationForm}
            serviceOptions={serviceOptions}
            serviceOptionsError={serviceOptionsError}
            onBack={previousStep}
            onNext={nextStep}
          />
        )}
        {currentStep === 3 && (
          <AddressStep
            form={addressForm}
            setForm={setAddressForm}
            onBack={previousStep}
            onNext={nextStep}
          />
        )}
        {currentStep === 4 && (
          <ReviewStep
            providerType={providerType}
            accessForm={accessForm}
            registrationForm={registrationForm}
            addressForm={addressForm}
            onBack={previousStep}
            onSubmit={handleSubmit}
            submitting={submitting}
            submitError={submitError}
            submitted={submitted}
          />
        )}
      </main>
    </div>
  );
}

function Hero({ theme }) {
  return (
    <section className="service-provider__hero" aria-label={theme.name}>
      <img className="service-provider__brand-logo" src={theme.logo} alt={theme.name} />
      {theme.watermark && <img className="service-provider__brand-watermark" src={theme.watermark} alt="" />}
    </section>
  );
}

function Stepper({ currentStep }) {
  return (
    <nav className="service-provider__steps" aria-label="Etapas do cadastro">
      {steps.map((step, index) => {
        const number = index + 1;
        const active = number === currentStep;

        return (
          <div className={`service-provider__step ${active ? 'is-active' : ''}`} key={step}>
            <span className="service-provider__step-line" />
            <span className="service-provider__step-number">{number}</span>
            <span className="service-provider__step-label">{step}</span>
          </div>
        );
      })}
    </nav>
  );
}

function AccessStep({ companyName, form, setForm, acceptedTerms, setAcceptedTerms, termsPaths, onNext }) {
  const [errors, setErrors] = useState({});

  // Os links abrem os documentos e ficam dentro do label do aceite: a propagacao
  // e interrompida para que o clique neles nunca marque o checkbox.
  const stopLabelToggle = event => event.stopPropagation();

  const updateField = (name, value) => {
    setForm(currentForm => ({ ...currentForm, [name]: value }));
    setErrors(currentErrors => ({ ...currentErrors, [name]: '' }));
  };

  const validateAccess = () => {
    const nextErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

    if (!emailRegex.test(form.email)) {
      nextErrors.email = 'Informe um e-mail válido.';
    }

    if (!passwordRegex.test(form.password)) {
      nextErrors.password = 'A senha deve ter no mínimo 8 caracteres, com letras e números.';
    }

    if (form.confirmPassword !== form.password) {
      nextErrors.confirmPassword = 'As senhas devem ser iguais.';
    }

    if (!acceptedTerms) {
      nextErrors.terms = 'Você precisa aceitar os Termos de Uso e a Política de Privacidade.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (validateAccess()) {
      onNext();
    }
  };

  return (
    <section className="service-provider__access">
      <h1>Cadastro de Prestador de Serviço</h1>
      <p>Cadastre-se para oferecer serviços de manutenção em imóveis administrados pela {companyName}.</p>

      <div className="service-provider__notice">
        <span><img src={shieldIcon} alt="" /></span>
        <p>Em poucos passos você estará cadastrado e pronto para receber novas oportunidades.</p>
      </div>

      <Field
        label="E-mail"
        required
        name="email"
        type="email"
        value={form.email}
        onChange={updateField}
        error={errors.email}
        icon={<MailOutlineIcon />}
      />
      <Field
        label="Senha"
        required
        name="password"
        type="password"
        value={form.password}
        onChange={updateField}
        error={errors.password}
        icon={<LockOutlinedIcon />}
        actionIcon={<VisibilityOutlinedIcon />}
        hint="Mínimo de 8 caracteres com letras, números e símbolos."
      />
      <Field
        label="Confirmar senha"
        required
        name="confirmPassword"
        type="password"
        value={form.confirmPassword}
        onChange={updateField}
        error={errors.confirmPassword}
        icon={<LockOutlinedIcon />}
        actionIcon={<VisibilityOutlinedIcon />}
      />

      <label className="service-provider__terms">
        <input
          type="checkbox"
          checked={acceptedTerms}
          aria-describedby={errors.terms ? 'service-provider-terms-error' : undefined}
          onChange={event => {
            setAcceptedTerms(event.target.checked);
            setErrors(currentErrors => ({ ...currentErrors, terms: '' }));
          }}
        />
        <strong>
          Li e concordo com os{' '}
          <Link to={termsPaths['Termos de Uso']} onClick={stopLabelToggle}>Termos de Uso</Link>
          {' '}e{' '}
          <Link to={termsPaths['Política de Privacidade']} onClick={stopLabelToggle}>Política de Privacidade</Link>.
        </strong>
      </label>
      {errors.terms && (
        <em className="service-provider__terms-error" id="service-provider-terms-error">
          {errors.terms}
        </em>
      )}

      <div className="service-provider__access-footer">
        <button className="service-provider__button service-provider__button--primary service-provider__button--wide" onClick={handleNext}>
          Continuar <ArrowForwardIcon />
        </button>
        <div className="service-provider__divider"><span>ou</span></div>
        <button className="service-provider__login-link">
          Já tenho cadastro <ArrowForwardIcon />
        </button>
        <p><LockOutlinedIcon /> Seus dados estão protegidos com segurança</p>
      </div>
    </section>
  );
}

function RegistrationStep({ providerType, onProviderTypeChange, form, setForm, serviceOptions, serviceOptionsError, onBack, onNext }) {
  const isLegal = providerType === 'legal';
  const [isProviderTypeOpen, setIsProviderTypeOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const selectProviderType = type => {
    onProviderTypeChange(type);
    setIsProviderTypeOpen(false);
    setErrors(currentErrors => ({ ...currentErrors, providerType: '' }));
  };

  const updateField = (name, value) => {
    let nextValue = value;

    if (name === 'cpf' || name === 'responsibleCpf') {
      nextValue = formatCpf(value);
    } else if (name === 'cnpj') {
      nextValue = formatCnpj(value);
    } else if (name === 'rg' || name === 'responsibleRg') {
      nextValue = onlyDigits(value);
    } else if (name === 'experience') {
      nextValue = onlyDigits(value).slice(0, 2);
    }

    setForm(currentForm => ({ ...currentForm, [name]: nextValue }));
    setErrors(currentErrors => ({ ...currentErrors, [name]: '' }));
  };

  const handleNext = () => {
    const nextErrors = {};
    const requiredFields = providerType === 'legal'
      ? [
        ['companyName', 'Informe a razão social ou nome fantasia.'],
        ['cnpj', 'Informe o CNPJ.'],
        ['responsibleName', 'Informe o nome do responsável.'],
        ['responsibleCpf', 'Informe o CPF do responsável.'],
        ['responsibleRg', 'Informe o RG do responsável.'],
      ]
      : [
        ['fullName', 'Informe o nome completo.'],
        ['cpf', 'Informe o CPF.'],
        ['rg', 'Informe o RG.'],
      ];

    if (!providerType) {
      nextErrors.providerType = 'Selecione o tipo de prestador.';
    } else {
      [...requiredFields,
        ['experience', 'Informe o tempo de atuação.'],
        ['description', 'Informe a descrição dos serviços.'],
      ].forEach(([name, message]) => {
        if (!form[name].trim()) {
          nextErrors[name] = message;
        }
      });

      if (form.services.length === 0) {
        nextErrors.services = 'Selecione ao menos um tipo de serviço.';
      }

      const documentFields = isLegal
        ? [['cnpj', Validate.isCNPJ, 'CNPJ inválido.'], ['responsibleCpf', Validate.isCPF, 'CPF do responsável inválido.']]
        : [['cpf', Validate.isCPF, 'CPF inválido.']];

      documentFields.forEach(([name, validate, message]) => {
        if (nextErrors[name]) return;

        const digits = onlyDigits(form[name]);
        const isRepeated = digits.length > 0 && digits.split('').every(digit => digit === digits[0]);
        const result = isRepeated ? false : validate(form[name]);

        if (result !== true) {
          nextErrors[name] = (result && result.message) || message;
        }
      });
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      onNext();
    }
  };

  return (
    <>
      <section className="service-provider__card">
        <SectionTitle title="Tipo de prestador" required />
        <div
          className="service-provider__select-wrapper"
          onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setIsProviderTypeOpen(false);
            }
          }}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              setIsProviderTypeOpen(false);
            }
          }}
        >
          <button
            type="button"
            className={`service-provider__select ${errors.providerType ? 'service-provider__select--error' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={isProviderTypeOpen}
            onClick={() => setIsProviderTypeOpen(open => !open)}
          >
            {providerType ? (isLegal ? 'Pessoa Jurídica' : 'Pessoa Física') : 'Selecione'}
            <KeyboardArrowDownIcon />
          </button>
          {isProviderTypeOpen && (
            <div className="service-provider__select-options" role="listbox" aria-label="Tipo de prestador">
              <button
                type="button"
                role="option"
                aria-selected={!isLegal}
                onClick={() => selectProviderType('physical')}
              >
                Pessoa Física
              </button>
              <button
                type="button"
                role="option"
                aria-selected={isLegal}
                onClick={() => selectProviderType('legal')}
              >
                Pessoa Jurídica
              </button>
            </div>
          )}
        </div>
        {errors.providerType && <em className="service-provider__select-error">{errors.providerType}</em>}

        {providerType && (
          <>
            <SectionTitle title={isLegal ? 'Dados da Empresa' : 'Dados Pessoais'} />
            {isLegal
              ? <LegalPersonFields form={form} errors={errors} onChange={updateField} />
              : <PhysicalPersonFields form={form} errors={errors} onChange={updateField} />}

            <SectionTitle title="Serviços" />
            <label className="service-provider__label">
              Tipos de serviços prestados <span className="service-provider__required-marker" aria-hidden="true">*</span>
            </label>
            <ServiceMultiSelect
              options={serviceOptions}
              loadError={serviceOptionsError}
              value={form.services}
              onChange={services => updateField('services', services)}
              error={errors.services}
            />
            <Field
              label="Tempo de atuação na área (em anos)"
              name="experience"
              required
              inputMode="decimal"
              value={form.experience}
              onChange={updateField}
              error={errors.experience}
            />
            <TextArea
              label={isLegal ? 'Descrição da empresa / serviços' : 'Descrição dos serviços'}
              name="description"
              required
              value={form.description}
              onChange={updateField}
              error={errors.description}
            />
          </>
        )}
      </section>
      <FooterActions onBack={onBack} onNext={handleNext} />
    </>
  );
}

function ServiceMultiSelect({ options, loadError, value, onChange, error }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const isLoading = !loadError && options.length === 0;
  const fieldError = error || loadError;
  const isSelected = option => value.some(service => service.id === option.id);
  const filteredOptions = options.filter(option => (
    !isSelected(option) && normalize(option.name).includes(normalize(query.trim()))
  ));
  const hasAvailableOptions = value.length < options.length;
  const shouldShowOptions = isOpen && !isLoading && (filteredOptions.length > 0 || query.trim().length > 0);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideInteraction = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
    };
  }, [isOpen]);

  const selectService = service => {
    onChange([...value, service]);
    setQuery('');
    setIsOpen(true);
  };

  const removeService = service => {
    onChange(value.filter(selectedService => selectedService.id !== service.id));
  };

  return (
    <div
      ref={containerRef}
      className="service-provider__services-select"
      onBlur={event => {
        // No iOS o toque em um botão não move o foco (relatedTarget nulo), e fechar
        // aqui desmontaria a lista antes do clique chegar na opção. Quem fecha nesse
        // caso é o listener de toque fora do componente.
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
          setIsOpen(false);
        }
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      }}
    >
      <div className={`service-provider__tag-field ${fieldError ? 'service-provider__tag-field--error' : ''}`}>
        {value.map(service => (
          <span key={service.id}>
            {service.name}
            <button type="button" aria-label={`Remover ${service.name}`} onClick={() => removeService(service)}>
              <CloseIcon />
            </button>
          </span>
        ))}
        {(hasAvailableOptions || isLoading) && (
          <input
            aria-label="Pesquisar tipos de serviços"
            aria-controls="service-provider-services-options"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            autoComplete="off"
            disabled={isLoading}
            placeholder={value.length === 0 ? (isLoading ? 'Carregando tipos de serviço...' : 'Digite para pesquisar') : ''}
            role="combobox"
            value={query}
            onFocus={() => setIsOpen(true)}
            onChange={event => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
          />
        )}
      </div>
      {shouldShowOptions && (
        <div className="service-provider__services-options" id="service-provider-services-options" role="listbox" aria-label="Tipos de serviços disponíveis">
          {filteredOptions.length > 0 ? filteredOptions.map(option => (
            <button
              type="button"
              role="option"
              aria-selected="false"
              key={option.id}
              onMouseDown={event => event.preventDefault()}
              onClick={() => selectService(option)}
            >
              {option.name}
            </button>
          )) : <p>Nenhum serviço encontrado.</p>}
        </div>
      )}
      {fieldError && <em>{fieldError}</em>}
    </div>
  );
}

function PhysicalPersonFields({ form, errors, onChange }) {
  return (
    <>
      <Field label="Nome Completo" name="fullName" required value={form.fullName} onChange={onChange} error={errors.fullName} />
      <div className="service-provider__grid service-provider__grid--half">
        <Field label="CPF" name="cpf" required value={form.cpf} onChange={onChange} error={errors.cpf} />
        <Field label="RG" name="rg" required maxLength={9} value={form.rg} onChange={onChange} error={errors.rg} />
      </div>
    </>
  );
}

function LegalPersonFields({ form, errors, onChange }) {
  return (
    <>
      <Field label="Razão Social / Nome Fantasia" name="companyName" required value={form.companyName} onChange={onChange} error={errors.companyName} />
      <Field label="CNPJ" name="cnpj" required value={form.cnpj} onChange={onChange} error={errors.cnpj} />
      <Field label="Nome do Responsável" name="responsibleName" required value={form.responsibleName} onChange={onChange} error={errors.responsibleName} />
      <div className="service-provider__grid service-provider__grid--half">
        <Field label="CPF do Responsável" name="responsibleCpf" required value={form.responsibleCpf} onChange={onChange} error={errors.responsibleCpf} />
        <Field label="RG do Responsável" name="responsibleRg" required maxLength={9} value={form.responsibleRg} onChange={onChange} error={errors.responsibleRg} />
      </div>
    </>
  );
}

function AddressStep({ form, setForm, onBack, onNext }) {
  const [errors, setErrors] = useState({});
  const [loadingCep, setLoadingCep] = useState(false);

  const searchCep = async (name, value) => {
    const cep = onlyDigits(value);

    if (cep.length !== 8) return;

    setLoadingCep(true);

    try {
      const address = await getAddressByCep(cep);

      if (address.erro) return;

      setForm(currentForm => ({
        ...currentForm,
        street: address.logradouro || currentForm.street,
        neighborhood: address.bairro || currentForm.neighborhood,
        city: address.localidade || currentForm.city,
        state: statesByUf[address.uf] || currentForm.state,
      }));
      setErrors(currentErrors => ({ ...currentErrors, street: '', neighborhood: '', city: '', state: '' }));
    } catch (error) {
      // busca de CEP é um facilitador: em caso de falha o endereço é preenchido manualmente
    } finally {
      setLoadingCep(false);
    }
  };

  const updateField = (name, value) => {
    let nextValue = value;

    if (name === 'cep') {
      nextValue = formatCep(value);
    } else if (name === 'number') {
      nextValue = onlyDigits(value);
    } else if (name === 'phone') {
      nextValue = formatPhone(value);
    }

    setForm(currentForm => ({ ...currentForm, [name]: nextValue }));
    setErrors(currentErrors => ({ ...currentErrors, [name]: '' }));
  };

  const handleNoNumberChange = checked => {
    setForm(currentForm => ({ ...currentForm, noNumber: checked, number: checked ? '' : currentForm.number }));
    setErrors(currentErrors => ({ ...currentErrors, number: '' }));
  };

  const handleNext = () => {
    const nextErrors = {};
    const requiredFields = [
      ['cep', 'Informe o CEP.'],
      ['street', 'Informe o logradouro.'],
      ['neighborhood', 'Informe o bairro.'],
      ['city', 'Informe a cidade.'],
      ['state', 'Selecione o estado.'],
      ['phone', 'Informe o telefone.'],
      ['pixKey', 'Informe a chave Pix.'],
    ];

    requiredFields.forEach(([name, message]) => {
      if (!form[name].trim()) nextErrors[name] = message;
    });

    if (!form.noNumber && !form.number.trim()) {
      nextErrors.number = 'Informe o número ou marque Sem número.';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      onNext();
    }
  };

  return (
    <>
      <section className="service-provider__card">
        <SectionTitle title="Endereço" />
        <Field label="CEP" name="cep" required small inputMode="numeric" maxLength={9} value={form.cep} onChange={updateField} onBlur={searchCep} error={errors.cep} />
        <div className="service-provider__grid service-provider__grid--address">
          <Field label="Logradouro" name="street" required disabled={loadingCep} value={form.street} onChange={updateField} error={errors.street} />
          <div className="service-provider__number-group">
            <Field label="Número" name="number" required inputMode="numeric" disabled={form.noNumber} value={form.number} onChange={updateField} error={errors.number} />
            <label className="service-provider__no-number">
              <input type="checkbox" checked={form.noNumber} onChange={event => handleNoNumberChange(event.target.checked)} />
              <span>Sem número</span>
            </label>
          </div>
        </div>
        <Field label="Complemento (Opcional)" name="complement" value={form.complement} onChange={updateField} />
        <div className="service-provider__grid service-provider__grid--half">
          <Field label="Bairro" name="neighborhood" required disabled={loadingCep} value={form.neighborhood} onChange={updateField} error={errors.neighborhood} />
          <Field label="Cidade" name="city" required disabled={loadingCep} value={form.city} onChange={updateField} error={errors.city} />
        </div>
        <StateSelect value={form.state} onChange={state => updateField('state', state)} error={errors.state} />

        <SectionTitle title="Contato" />
        <Field label="Telefone" name="phone" required inputMode="numeric" maxLength={15} value={form.phone} onChange={updateField} error={errors.phone} />

        <SectionTitle title="Dados para Pagamento" />
        <Field label="Informe sua chave pix" name="pixKey" required maxLength={32} value={form.pixKey} onChange={updateField} error={errors.pixKey} />
        <p className="service-provider__help">Os serviços poderão ser pagos pelos usuários do app via pix, através da chave informada</p>
      </section>
      <FooterActions onBack={onBack} onNext={handleNext} />
    </>
  );
}

function StateSelect({ value, onChange, error }) {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value) setQuery(value);
  }, [value]);

  const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filteredStates = brazilianStates.filter(state => normalize(state).includes(normalize(query.trim())));

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideInteraction = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
    };
  }, [isOpen]);

  const selectState = state => {
    setQuery(state);
    onChange(state);
    setIsOpen(false);
  };

  return (
    <div className="service-provider__state-select" ref={containerRef}>
      <label htmlFor="service-provider-state">
        Estado <span aria-hidden="true">*</span>
      </label>
      <div className={error ? 'is-error' : ''}>
        <input
          id="service-provider-state"
          role="combobox"
          aria-controls="service-provider-state-options"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          autoComplete="off"
          placeholder="Digite para pesquisar"
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={event => {
            setQuery(event.target.value);
            onChange('');
            setIsOpen(true);
          }}
        />
        <KeyboardArrowDownIcon />
      </div>
      {isOpen && (
        <div className="service-provider__state-options" id="service-provider-state-options" role="listbox" aria-label="Estados disponíveis">
          {filteredStates.length > 0 ? filteredStates.map(state => (
            <button type="button" role="option" aria-selected={state === value} key={state} onClick={() => selectState(state)}>
              {state}
            </button>
          )) : <p>Nenhum estado encontrado.</p>}
        </div>
      )}
      {error && <em>{error}</em>}
    </div>
  );
}

function ReviewStep({ providerType, accessForm, registrationForm, addressForm, onBack, onSubmit, submitting, submitError, submitted }) {
  const personRows = providerType === 'legal'
    ? [
      ['Razão Social / Nome Fantasia', registrationForm.companyName],
      ['CNPJ', registrationForm.cnpj],
      ['Nome do Responsável', registrationForm.responsibleName],
      ['CPF do Responsável', registrationForm.responsibleCpf],
      ['RG do Responsável', registrationForm.responsibleRg],
    ]
    : [
      ['Nome Completo', registrationForm.fullName],
      ['CPF', registrationForm.cpf],
      ['RG', registrationForm.rg],
    ];
  const registrationRows = [
    ['Tipo de Prestador', providerType === 'legal' ? 'Pessoa Jurídica' : 'Pessoa Física'],
    ...personRows,
    ['Tipos de serviços prestados', registrationForm.services.map(service => service.name).join(', ')],
    ['Tempo de atuação na área', `${registrationForm.experience} anos`],
    [providerType === 'legal' ? 'Descrição da empresa / serviços' : 'Descrição dos serviços', registrationForm.description],
  ];
  const addressRows = [
    ['CEP', addressForm.cep],
    ['Logradouro', addressForm.street],
    ['Número', addressForm.noNumber ? 'Sem número' : addressForm.number],
    ...(addressForm.complement ? [['Complemento', addressForm.complement]] : []),
    ['Bairro', addressForm.neighborhood],
    ['Cidade', addressForm.city],
    ['Estado', addressForm.state],
    ['Telefone', addressForm.phone],
  ];

  if (submitted) {
    return (
      <section className="service-provider__card service-provider__success">
        <img src={shieldIcon} alt="" />
        <h2>Cadastro enviado com sucesso!</h2>
        <p>
          Seus dados foram recebidos e estão em análise. Assim que o cadastro for aprovado,
          você poderá acessar a plataforma com o e-mail <strong>{accessForm.email}</strong>.
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="service-provider__card service-provider__review">
        <SectionTitle title="Acesso:" />
        <ReviewRows rows={[['E-mail', accessForm.email]]} />
        <SectionTitle title="Dados cadastrais" />
        <ReviewRows rows={registrationRows} />
        <SectionTitle title="Endereço e Contato" />
        <ReviewRows rows={addressRows} />
        <SectionTitle title="Dados para pagamento" />
        <ReviewRows rows={[['Chave Pix', addressForm.pixKey]]} />
        {submitError && <p className="service-provider__submit-error" role="alert">{submitError}</p>}
      </section>
      <FooterActions onBack={onBack} onNext={onSubmit} nextLabel={submitting ? 'Enviando...' : 'Enviar'} disabled={submitting} />
    </>
  );
}

function Field({ label, name, type, value, onChange, onBlur, error, icon, actionIcon, hint, small, required, inputMode, maxLength, disabled }) {
  const fieldName = name || label;
  const [visible, setVisible] = useState(false);
  const inputType = type === 'password' && visible ? 'text' : (type || 'text');
  const hasPasswordToggle = type === 'password' && actionIcon;
  const acceptsOnlyNumbers = ['cpf', 'rg', 'cnpj', 'responsibleCpf', 'responsibleRg'].includes(fieldName);
  const fieldMaxLength = maxLength || (fieldName === 'cnpj'
    ? 18
    : (fieldName === 'cpf' || fieldName === 'responsibleCpf' ? 14 : undefined));

  return (
    <label className={`service-provider__field ${small ? 'service-provider__field--small' : ''} ${error ? 'service-provider__field--error' : ''} ${disabled ? 'service-provider__field--disabled' : ''}`}>
      <span>
        {label} {required && <span className="service-provider__required-marker" aria-hidden="true">*</span>}
      </span>
      <div>
        {icon && <i>{icon}</i>}
        <input
          aria-label={label}
          name={fieldName}
          type={inputType}
          inputMode={inputMode || (acceptsOnlyNumbers ? 'numeric' : undefined)}
          maxLength={fieldMaxLength}
          disabled={disabled}
          placeholder={disabled ? '' : fieldPlaceholders[label]}
          value={value}
          onChange={event => onChange && onChange(fieldName, event.target.value)}
          onBlur={event => onBlur && onBlur(fieldName, event.target.value)}
        />
        {hasPasswordToggle && (
          <button
            className="service-provider__field-action"
            type="button"
            aria-label={visible ? 'Ocultar senha' : 'Exibir senha'}
            onClick={() => setVisible(currentVisible => !currentVisible)}
          >
            {visible ? <VisibilityOffOutlinedIcon /> : actionIcon}
          </button>
        )}
        {actionIcon && !hasPasswordToggle && <span className="service-provider__field-action">{actionIcon}</span>}
      </div>
      {hint && <small>{hint}</small>}
      {error && <em>{error}</em>}
    </label>
  );
}

function TextArea({ label, name, value, onChange, error, required }) {
  const fieldName = name || label;

  return (
    <label className={`service-provider__field service-provider__field--textarea ${error ? 'service-provider__field--error' : ''}`}>
      <span>
        {label} {required && <span className="service-provider__required-marker" aria-hidden="true">*</span>}
      </span>
      <textarea
        aria-label={label}
        name={fieldName}
        placeholder={fieldPlaceholders[label]}
        value={value}
        onChange={event => onChange && onChange(fieldName, event.target.value)}
      />
      {error && <em>{error}</em>}
    </label>
  );
}

function SectionTitle({ title, required }) {
  return (
    <div className="service-provider__section-title">
      <h2>
        {title} {required && <span className="service-provider__required-marker" aria-hidden="true">*</span>}
      </h2>
    </div>
  );
}

function ReviewRows({ rows }) {
  return (
    <dl className="service-provider__review-rows">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function FooterActions({ onBack, onNext, nextLabel, disabled }) {
  return (
    <div className="service-provider__footer-actions">
      <button type="button" className="service-provider__button service-provider__button--secondary" onClick={onBack} disabled={disabled}>
        <ArrowBackIcon /> Voltar
      </button>
      <button type="button" className="service-provider__button service-provider__button--primary" onClick={onNext} disabled={disabled}>
        {nextLabel || 'Continuar'} {!nextLabel && <ArrowForwardIcon />}
      </button>
    </div>
  );
}

export default ServiceProvider;
